import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import UnfoldMoreOutlinedIcon from "@mui/icons-material/UnfoldMoreOutlined";
import {
  Checkbox,
  IconButton,
  Snackbar,
  SnackbarContent,
  Stack,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CloseIcon from "@mui/icons-material/Close";
import Roles from "../../../../../common/enums/Roles";
import { CheckForStockist } from "../../../../../common/utils/ReportsFilters";
import Panels from "../../../../../common/enums/Panels";
import CustomerType from "../../../../../common/enums/CustomerType";
import { CustomerDatabaseStore } from "../../../../../store/main/CustomerDatabaseStore";
import CustomerTableDetailMobile from "./CustomerTableDetailMobile";
import {
  CustomerDetails,
  CustomerMetadataType,
} from "../../../../../domain/models/customer-database/customer-details";
import ReportScreen from "../../../../../common/enums/ReportScreens";

type Props = {
  panelName: string;
  type: string;
};

const CustomerTableMobile: React.FC<Props> = ({ panelName, type }) => {
  const {
    customerData,
    handleSelectedCustomers,
    userRole,
    filterStore,
    handleSelectedCustomersMappingIds,
    filterSegement,
    handleShowSegement,
    handleShowAddSegement,
    customerMetadata,
  } = CustomerDatabaseStore();
  const [openAlert, setOpenAlert] = useState(false);
  const [selectedCards, setSelectedCards] = useState<number[]>([]);
  const [selectedMappingIds, setSelectedMappingIds] = useState<number[]>([]);
  const limitToSelectCustomer = 20;
  const [showSegement, setShowSegement] = useState<boolean>(false);

  const showButton = () => {
    if (filterSegement.length > 0 && selectedCards.length !== 0) {
      const arr = customerData.find(
        (data: CustomerDetails) => data.id === selectedCards[0]
      );
      const key = filterSegement[0] as keyof CustomerMetadataType["metadata"];
      const segmentValue = arr?.customer_metadata.find((meta) => {
        return meta.metadata[key] === 0 || meta.metadata[key] === 1;
      })?.metadata[key];

      if (segmentValue) {
        handleShowAddSegement(false);
      } else {
        handleShowAddSegement(true);
      }
      handleShowSegement(true);
    } else {
      handleShowSegement(false);
    }
  };

  useEffect(() => {
    showButton();
  }, [customerData, selectedCards]);

  const selectAllRows = (checked: boolean) => {
    const mappingIds = [...selectedMappingIds];
    const values = [...selectedCards];
    values.splice(0, values.length);
    if (checked) {
      for (let i = 0; i < customerData.length; i++) {
        if (
          values.length < limitToSelectCustomer &&
          customerData[i].customer_type.name != CustomerType.STOCKIST
        ) {
          values.push(customerData[i].id);
        }
      }
    }
    mappingIds.splice(0, mappingIds.length);
    if (checked) {
      for (let i = 0; i < customerData.length; i++) {
        if (
          mappingIds.length < limitToSelectCustomer &&
          customerData[i].customer_type.name != CustomerType.STOCKIST
        ) {
          mappingIds.push(customerData[i].mapping_id);
        }
      }
    }
    setSelectedCards(values);
    handleSelectedCustomers(values);
    handleSelectedCustomersMappingIds(mappingIds);
    setSelectedMappingIds(mappingIds);
  };

  const selectSingleRow = (checked: boolean, id: number, mappingId: number) => {
    let value = [...selectedCards];
    let mappingIds = [...selectedMappingIds];
    if (value.length === limitToSelectCustomer) {
      setOpenAlert(true);
    }

    if (checked && value.length < limitToSelectCustomer) {
      value.push(id);
      mappingIds.push(mappingId);
    } else {
      const filtered = value.filter((data) => data !== id);
      const filteredMappingID = mappingIds.filter((data) => data !== mappingId);

      value = filtered;
      mappingIds = filteredMappingID;
    }
    setSelectedCards(value);
    setSelectedMappingIds(mappingIds);
    handleSelectedCustomers(value);
    handleSelectedCustomersMappingIds(mappingIds);
  };

  const handleCloseSnackbar = () => {
    setOpenAlert(false);
  };

  useEffect(() => {
    if (selectedCards.length === limitToSelectCustomer) {
      setOpenAlert(true);
    }
  }, [selectedCards.length]);

  useEffect(() => {
    if (filterSegement.length > 0) {
      const segement = filterSegement[0];
      const metaData = customerMetadata.find((meta) => meta.code === segement)
        ?.configuration.customer_type_ids;
      if (metaData?.find((meta) => meta === filterStore.customer_type_id)) {
        setShowSegement(true);
      } else {
        setShowSegement(false);
      }
    }
  }, []);

  return (
    <>
      <Snackbar
        open={openAlert}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <SnackbarContent
          style={{
            backgroundColor: "lightcoral",
          }}
          message={
            <Stack direction={"row"} spacing={1} alignItems={"center"}>
              <ErrorOutlineIcon color="inherit" />
              <Typography fontWeight={600} color={"white"}>
                You can select maximum {limitToSelectCustomer} customers only
              </Typography>
              <IconButton
                size="small"
                aria-label="close"
                color="inherit"
                onClick={handleCloseSnackbar}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>
          }
        />
      </Snackbar>
      <TableContainer component={Paper} sx={{ boxShadow: "none" }}>
        <Box sx={{ overflow: "scroll", height: "60vh" }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    color="primary"
                    disabled={
                      CheckForStockist(customerData).length === 0 ||
                      userRole.role === Roles.HEADQUATER_MANAGER ||
                      filterSegement.length !== 0
                    }
                    checked={
                      selectedCards.length > 0 &&
                      (selectedCards.length === limitToSelectCustomer ||
                        selectedCards.length === customerData.length)
                    }
                    onChange={(e) => selectAllRows(e.target.checked)}
                  />
                </TableCell>
                <TableCell>
                  <UnfoldMoreOutlinedIcon />
                </TableCell>
                <TableCell align="center">
                  <Typography variant="subtitle2" color="primary.dark">
                    Code
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="subtitle2" color="primary.dark">
                    Name
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="subtitle2" color="primary.dark">
                    Phone
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="subtitle2" color="primary.dark">
                    Type
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="subtitle2" color="primary.dark">
                    Brick/HQ
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="subtitle2" color="primary.dark">
                    Address
                  </Typography>
                </TableCell>
                {type === ReportScreen.CUSTOMER_DATABASE && (
                  <>
                    <TableCell align="center">
                      <Typography variant="subtitle2" color="primary.dark">
                        Speciality
                      </Typography>
                    </TableCell>
                    {showSegement && (
                      <>
                        <TableCell align="center">
                          <Typography variant="subtitle2" color="primary.dark">
                            {
                              customerMetadata.find(
                                (meta) => meta.code === filterSegement[0]
                              )?.name
                            }
                          </Typography>
                        </TableCell>
                      </>
                    )}
                    {panelName === Panels.ALL ||
                    panelName === Panels.ARCHIVED ||
                    panelName === Panels.PENDING_MAPPING ||
                    panelName === Panels.PENDING_APPROVAL ? (
                      <TableCell align="center">
                        <Typography variant="subtitle2" color="primary.dark">
                          Actions
                        </Typography>
                      </TableCell>
                    ) : (
                      <TableCell align="center">
                        <Typography variant="subtitle2" color="primary.dark">
                          -
                        </Typography>
                      </TableCell>
                    )}
                  </>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {customerData.map((row: any) => {
                return (
                  <CustomerTableDetailMobile
                    type={type}
                    row={row}
                    selectedCards={selectedCards}
                    selectSingleRow={selectSingleRow}
                    panelName={panelName}
                    showSegement={showSegement}
                  />
                );
              })}
            </TableBody>
          </Table>
        </Box>
      </TableContainer>
    </>
  );
};
export default CustomerTableMobile;
